import { HelpOutline } from '@mui/icons-material';
import { Typography, Divider, Grid, Link } from '@mui/material';
import Tippy from '@tippyjs/react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { Svgs } from '../../../../assets/svg';
import { Column, Row } from '../../../../components';
import { Colors, Spacing } from '../../../../themes';
import { UpsellInvestmentAdviceModal } from '../../../holistic-financial-advice/investments/InvestmentMixModals';
import { Button } from '../../../../components/buttons/Button';
import UpsellCard from './UpsellCard';

interface DesktopUpsellContentProps {
  advicePrice: string;
  openDoc: (description: 'Terms of Service' | 'Privacy Policy') => Promise<void>;
  onUpgradeToPremium: () => Promise<void>;
  handleClose?: () => void;
  sbsa?: boolean;
}

const DesktopUpsellContent: React.FC<DesktopUpsellContentProps> = ({
  advicePrice,
  openDoc,
  onUpgradeToPremium,
  handleClose,
  sbsa,
}) => {
  return (
    <Column
      margin={'15px 100px'}
      sx={{
        alignItems: 'center',
      }}>
      {/* Title */}
      <Typography
        textAlign={'center'}
        variant='p40Bold'
        color='secondary'
        mb={'20px'}
        mt={sbsa ? 1 : 12}>
        {t('YourSmartPortfolio')}
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          display: 'inline-flex',
          textAlign: 'center',
          marginBottom: '30px',
          alignItems: 'center',
        }}
        variant='p18SemiBold'
        color='primary'>
        {t('AIPrecision')}
        <Grid sx={{ display: 'inline-flex' }} ml={'5px'}>
          <Tippy
            placement='right'
            interactive={true}
            content={<UpsellInvestmentAdviceModal />}
            theme='light-nickels-theme'>
            <HelpOutline
              sx={{
                color: Colors.primaryBase,
              }}
            />
          </Tippy>
        </Grid>
      </Typography>

      {/* Cards */}
      <Row style={{ marginBottom: '50px' }}>
        <UpsellCard
          art={<Svgs.ArtCardCash />}
          title={t('Cash')}
          cardStyle={{ marginRight: '-20px', zIndex: 2 }}
        />
        <UpsellCard
          art={<Svgs.ArtCardBonds />}
          title={t('Bonds')}
          cardStyle={{ marginTop: '50px', marginBottom: '-50px', zIndex: 3 }}
        />
        <UpsellCard
          art={<Svgs.ArtCardStocks />}
          title={t('Stocks')}
          cardStyle={{ marginLeft: '-20px', zIndex: 2 }}
        />
      </Row>

      {/* List */}
      <Row style={{ marginTop: '30px' }}>
        <Typography variant='p16' color='primary' textAlign='center'>
          {t('UnlockFundRecommendations')}
        </Typography>
        <Divider
          orientation='vertical'
          style={{ borderColor: Colors.secondaryBase, margin: '0 30px', width: '2px' }}
          flexItem
        />
        <Typography variant='p16' color='primary' textAlign='center'>
          {t('PersonalizedAdviceOnAllAccounts')}
        </Typography>
        <Divider
          orientation='vertical'
          style={{ borderColor: Colors.secondaryBase, margin: '0 30px', width: '2px' }}
          flexItem
        />
        <Typography variant='p16' color='primary' textAlign='center'>
          {t('FlatFeeNoMatterYourBalance')}
        </Typography>
      </Row>

      {/* Price */}
      <Typography
        mt={6}
        sx={{ alignItems: 'baseline' }}
        variant='p26BoldMontserrat'
        color='secondary'>
        {advicePrice}
        <Typography variant='p16Montserrat' color='secondary'>
          {t('perMonth')}
        </Typography>
      </Typography>

      {/* Disclaimer */}
      <Typography
        sx={{ textAlign: 'center', display: 'inline', minWidth: '300px' }}
        mt={'10px'}
        mb={'30px'}
        mx={Spacing.md}
        variant='p12'
        color='secondary'>
        {t('ThisSubscriptionRenewsAutomatically', {
          interval: t('month'),
        })}{' '}
        <Typography className='link-wrapper' variant='p12' color='secondary'>
          <Trans
            i18nKey='PleaseSeeTOSAndPrivacyPolicy'
            components={{
              TermsOfService: <Link onClick={() => openDoc('Terms of Service')} />,
              PrivacyPolicy: <Link onClick={() => openDoc('Privacy Policy')} />,
            }}
          />
        </Typography>
      </Typography>

      {/* Buttons */}
      <Button onClick={onUpgradeToPremium} color='primary' style={{ minWidth: '200px' }}>
        <Svgs.DisplaySmallPrimaryLightLock style={{ marginRight: '10px' }} />
        {t('UpgradeToPremium')}
      </Button>
      {handleClose && (
        <Button
          style={{ marginTop: '20px', minWidth: '200px' }}
          color='primary'
          variant='ghost'
          onClick={handleClose}>
          {t('NotNow')}
        </Button>
      )}
    </Column>
  );
};

export default DesktopUpsellContent;
